/** The VITE_* environment variables, read once into a typed object. */

interface Environment {
  apiBaseUrl: string;
  appName: string;
  isDevelopment: boolean;
  isProduction: boolean;
  enableDevtools: boolean;
  sentryDsn?: string;
  debug: boolean;
  forceHttps: boolean;
  allowedOrigins: string[];
}

/**
 * Vite exposes only VITE_-prefixed variables.
 *
 * The API URL:
 * - If VITE_API_BASE_URL is set, use it (explicit configuration)
 * - Otherwise, in development use port 8000, in production use /api/v1 (proxied)
 * This allows the app to work when accessed from different machines
 */
function getApiBaseUrl(): string {
  // Check if explicit API URL is configured
  const configuredUrl = import.meta.env.VITE_API_BASE_URL;
  if (configuredUrl) {
    return configuredUrl;
  }

  // Fallback: Use same-origin API in production, or localhost:8000 in development
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    
    // In production, API should be on same origin (proxied by Traefik/Nginx)
    // In development, use port 8000
    const isDev = import.meta.env.DEV;
    if (isDev || hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:8000/api/v1`;
    }
    
    // Production: use same-origin (will be proxied)
    return '/api/v1';
  }

  return '/api/v1';
}

export const env: Environment = {
  apiBaseUrl: getApiBaseUrl(),
  appName: import.meta.env.VITE_APP_NAME || 'Library Management System',
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  enableDevtools: import.meta.env.VITE_ENABLE_DEVTOOLS === 'true',
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  debug: import.meta.env.VITE_DEBUG === 'true',
  forceHttps: import.meta.env.VITE_FORCE_HTTPS === 'true',
  allowedOrigins: import.meta.env.VITE_ALLOWED_ORIGINS?.split(',') || [],
};

/** Types for import.meta.env. */
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL: string;
    readonly VITE_APP_NAME: string;
    readonly VITE_DEBUG: string;
    readonly VITE_ENABLE_DEVTOOLS: string;
    readonly VITE_SENTRY_DSN?: string;
    readonly VITE_FORCE_HTTPS: string;
    readonly VITE_ALLOWED_ORIGINS?: string;
  }
}

/**
 * Validates that required environment variables are present
 * Throws an error if critical variables are missing
 */
export function validateEnvironment(): void {
  if (!env.apiBaseUrl) {
    throw new Error('VITE_API_BASE_URL is required but not configured');
  }
  
  // Validate HTTPS in production (Requirement 5.1)
  // We allow disabling this check if VITE_FORCE_HTTPS is explicitly set to 'false'
  const httpsCheckDisabled = import.meta.env.VITE_FORCE_HTTPS === 'false';
  
  if ((env.isProduction && !httpsCheckDisabled) || env.forceHttps) {
    if (!env.apiBaseUrl.startsWith('https://')) {
      throw new Error(
        'VITE_API_BASE_URL must use HTTPS in production. ' +
        `Current value: ${env.apiBaseUrl}. ` +
        'To test in production mode via HTTP, set VITE_FORCE_HTTPS=false in connection with your backend.'
      );
    }
    
    // Validate current page is HTTPS
    if (typeof window !== 'undefined' && window.location.protocol !== 'https:') {
      console.error('Application must be served over HTTPS in production');
      // Redirect to HTTPS if possible
      if (env.forceHttps) {
        const httpsUrl = window.location.href.replace('http://', 'https://');
        window.location.replace(httpsUrl);
      }
    }
  }
  
  // Only warn about Sentry if in production and not explicitly configured
  if (env.isProduction && !env.sentryDsn) {
    console.info('Optional: VITE_SENTRY_DSN can be configured for error tracking in production');
  }
  
  // Validate allowed origins in production
  // Note: This warning is informational only if using same-origin API
  if (env.isProduction && env.allowedOrigins.length === 0) {
    console.info('Info: VITE_ALLOWED_ORIGINS not set. Using same-origin policy.');
  }
}

/** Rewrites http:// to https:// in production, or when VITE_FORCE_HTTPS is set. */
export function ensureHttps(url: string): string {
  if (!url) return url;
  
  // In production or when HTTPS is forced, ensure HTTPS
  if (env.isProduction || env.forceHttps) {
    if (url.startsWith('http://')) {
      return url.replace('http://', 'https://');
    }
  }
  
  return url;
}

/** Whether an origin is in VITE_ALLOWED_ORIGINS. Anything goes in development. */
export function isOriginAllowed(origin: string): boolean {
  // In development, allow all origins
  if (env.isDevelopment) {
    return true;
  }
  
  // In production, check against allowed origins
  if (env.allowedOrigins.length === 0) {
    // If no origins configured, only allow same origin
    return origin === window.location.origin;
  }
  
  return env.allowedOrigins.includes(origin);
}

// Validate environment on module load
validateEnvironment();
